import { Link, Navigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import PublicSiteHeader from '../../components/public/PublicSiteHeader';
import PublicFiliereTrackProgrammes from '../../components/public/PublicFiliereTrackProgrammes';
import PublicFiliereTrackHero from '../../components/public/PublicFiliereTrackHero';
import PublicFiliereTrackAbout from '../../components/public/PublicFiliereTrackAbout';
import PublicSiteFooter from '../../components/public/PublicSiteFooter';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import { getDomaineMetaBySlug } from '../../constants/academiaFiliereDomains';
import { usePublicFilieres } from '../../hooks/usePublicFilieres';
import { usePublicTarifsWithFiliereOverrides } from '../../hooks/usePublicTarifs';
import { usePageMeta } from '../../hooks/usePageMeta';
import { fetchFilierePlanning } from '../../services/academiaApprenantService';
import type { PublicPlanningSession } from '../../utils/publicPlanningDisplay';
import '../../styles/publicFiliereTrackPage.css';

type Props = {
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  spaceLabel?: string;
  onGoToSpace?: () => void;
};

export default function FiliereTrackPage({ isAuthenticated, isAdmin, spaceLabel, onGoToSpace }: Props) {
  const { trackSlug } = useParams<{ trackSlug: string }>();
  const { filieres, domaines, cohortes, tracks, loading, error, reload } = usePublicFilieres();
  const { categoriesForFiliere, defaultCategories } = usePublicTarifsWithFiliereOverrides();

  const track = useMemo(() => {
    const fromApi = tracks.find((t) => t.slug === trackSlug);
    if (fromApi) return fromApi;
    const meta = getDomaineMetaBySlug(trackSlug, domaines);
    if (!meta) return undefined;
    const domainFilieres = filieres.filter(
      (f) => f.estActive !== false && f.domaine?.toUpperCase() === meta.domaineKey,
    );
    if (domainFilieres.length === 0) return undefined;
    return { ...meta, filieres: domainFilieres };
  }, [tracks, trackSlug, filieres, domaines]);

  const [planningByFiliereId, setPlanningByFiliereId] = useState<Record<string, PublicPlanningSession[]>>({});

  usePageMeta({
    title: track?.title ?? 'Formations',
    description: track
      ? `Formations professionnelles certifiantes — ${track.title}. Parcourez le programme et inscrivez-vous en ligne à Kinshasa.`
      : undefined,
    canonicalPath: trackSlug ? `/filieres/${trackSlug}` : undefined,
  });

  useEffect(() => {
    if (!track?.filieres?.length) {
      setPlanningByFiliereId({});
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const entries = await Promise.all(
          track.filieres.map(async (f) => {
            const rows = await fetchFilierePlanning(f.id);
            return [f.id, Array.isArray(rows) ? (rows as PublicPlanningSession[]) : []] as const;
          }),
        );
        if (!cancelled) setPlanningByFiliereId(Object.fromEntries(entries));
      } catch {
        if (!cancelled) setPlanningByFiliereId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [track]);

  if (!loading && !track && !getDomaineMetaBySlug(trackSlug, domaines)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <PublicSiteHeader
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        spaceLabel={spaceLabel}
        onGoToSpace={onGoToSpace}
      />

      {loading ? (
        <div className="mx-auto max-w-6xl px-4 py-16 text-center text-sm text-on-surface-variant sm:px-6">
          Chargement des formations…
        </div>
      ) : error ? (
        <div className="mx-auto max-w-6xl px-4 py-16 text-center sm:px-6">
          <p className="text-sm text-on-surface-variant">Impossible de charger le catalogue des formations.</p>
          <button
            type="button"
            onClick={() => void reload()}
            className="mt-4 rounded-public-btn bg-primary px-5 py-2.5 text-sm font-bold text-white"
          >
            Réessayer
          </button>
        </div>
      ) : track ? (
        <>
          <PublicFiliereTrackHero track={track} cohortes={cohortes} />
          <PublicFiliereTrackAbout track={track} />

          <PublicFiliereTrackProgrammes
            track={track}
            cohortes={cohortes}
            planningByFiliereId={planningByFiliereId}
            pricingCategories={defaultCategories}
            pricingCategoriesForFiliere={categoriesForFiliere}
          />
        </>
      ) : (
        <div className="mx-auto max-w-6xl px-4 py-16 text-center text-sm text-on-surface-variant sm:px-6">
          Aucune formation active dans cette famille pour le moment.
          <Link to="/#filieres" className="mt-4 block font-bold text-primary hover:underline">
            Retour aux filières
          </Link>
        </div>
      )}

      <PublicSiteFooter />

      <ScrollToTopButton />
    </div>
  );
}
